import { Injectable } from '@angular/core';
import {
  AgGridAggregateFilter,
  AgGridFilterAggregate,
  AgGridFilterModel,
  AgGridSimpleDateFilter, Range,
  WhereAggregateOperator,
} from '@anduin/shared';
import { ConfigService } from '@anduin/config';
import {
  EMAIL_TYPE_GROUPS,
  EmailDeliveryHubSentFilters,
  isEmailTypeEnabled,
} from '../../../models/email-delivery-hub-filters.models';
import { OutboundEmailStatus } from '../../../models/outbound-emails.model';
import { ALL_EMAIL_TYPES_LABEL } from '../../../utils/all-email-types';


@Injectable()
export class EmailDeliveryHubAgGridFilterAggregateFactory {
  constructor(private readonly configService: ConfigService) {}

  create(filters: Partial<EmailDeliveryHubSentFilters>): AgGridFilterAggregate {
    const conditions: (AgGridFilterModel | AgGridAggregateFilter)[] = [];

    const dateRange: Range<string> = {
      min: filters.dateRange?.min ?? filters.min,
      max: filters.dateRange?.max ?? filters.max,
    };

    if (dateRange.min && dateRange.max) {
      conditions.push({
        column: 'sentAt',
        filter: {
          filterType: 'date',
          type: 'inRange',
          filter: String(dateRange.min),
          filterTo: String(dateRange.max),
        } as AgGridSimpleDateFilter,
      });
    } else if (dateRange.min) {
      conditions.push({
        column: 'sentAt',
        filter: { filterType: 'date', type: 'greaterThanOrEqual', filter: String(dateRange.min) },
      });
    } else if (dateRange.max) {
      conditions.push({
        column: 'sentAt',
        filter: { filterType: 'date', type: 'lessThanOrEqual', filter: String(dateRange.max) },
      });
    } else if (!dateRange.min && !dateRange.max) {
      // Default to last 30 days if no date filters are set
      const defaultMax = new Date();
      const defaultMin = new Date();
      defaultMin.setDate(defaultMin.getDate() - 30);

      conditions.push({
        column: 'sentAt',
        filter: {
          filterType: 'date',
          type: 'inRange',
          filter: String(defaultMin),
          filterTo: String(defaultMax),
        } as AgGridSimpleDateFilter,
      });
    }

    if (filters.showBouncedOnly) {
      conditions.push({
        column: 'status',
        filter: {
          filterType: 'text',
          type: 'equals',
          filter: OutboundEmailStatus.BOUNCED,
        },
      });
    }

    return conditions.length > 0 ? { operator: WhereAggregateOperator.AND, conditions } : undefined;
  }

  createTypeFilter({ types }: Partial<EmailDeliveryHubSentFilters>): AgGridFilterModel {
    if (!types || (types.length === 1 && types[0] === ALL_EMAIL_TYPES_LABEL)) {
      return undefined;
    }

    const expandedTypes = types
      .flatMap((type) => EMAIL_TYPE_GROUPS[type] ?? [type])
      .filter((type) => isEmailTypeEnabled(type, this.configService));

    if (expandedTypes.length === 0) {
      return undefined;
    }

    return {
      column: 'emailType',
      filter: {
        filterType: 'array',
        type: 'in',
        filter: expandedTypes,
      },
    };
  }
}
